import { DateTime } from 'luxon';
import {
  AnyAction,
  combineReducers
} from 'redux';

import {
  isClearRecentlyModified,
  isCreateUserDefinedRoleSuccess,
  isGetUserDefinedRolesSuccess,
  isGetUserDefinedRoleSuccess,
  isUpdateUserDefinedRoleSuccess
} from './actions';
import {
  UserDefinedRole,
  UserDefinedRoleResponseDto,
  UserDefinedRoles
} from './state';

export const INITIAL_STATE: UserDefinedRoles = {
  allCodes: [],
  byCode: {},
  recentlyModified: []
};

// Utility Functions

export function parseUserDefinedRole(
  userDefinedRole: UserDefinedRoleResponseDto
): UserDefinedRole {
  return {
    ...userDefinedRole,
    created: DateTime.fromISO(userDefinedRole.created),
    updated: DateTime.fromISO(userDefinedRole.updated)
  };
}

// Reducers

function byCode(state = INITIAL_STATE.byCode, action: AnyAction): UserDefinedRoles['byCode'] {
  // add all new codes
  if (isGetUserDefinedRolesSuccess(action)) {
    const updatedUserDefinedRoles = action.payload.data;
    // replace the entire previous state so as to not keep any stale data
    const updatedState = {} as UserDefinedRoles['byCode'];
    for (const userRole of updatedUserDefinedRoles) {
      const code = userRole.code;
      updatedState[code] = parseUserDefinedRole(userRole);
    }
    return updatedState;
  }

  // add the newly created user-defined role
  if (isCreateUserDefinedRoleSuccess(action) || isUpdateUserDefinedRoleSuccess(action)) {
    const userDefinedRole = parseUserDefinedRole(action.payload.data);
    const code = userDefinedRole.code;
    return { ...state, [code]: userDefinedRole };
  }

  if (isGetUserDefinedRoleSuccess(action)) {
    const userDefinedRole = parseUserDefinedRole(action.payload.data);
    const code = userDefinedRole.code;
    return { ...state, [code]: userDefinedRole };
  }

  return state;
}
function allCodes(state = INITIAL_STATE.allCodes, action: AnyAction): UserDefinedRoles['allCodes'] {
  // add all new codes
  if (isGetUserDefinedRolesSuccess(action)) {
    return action.payload.data.map(d => d.code);
  }

  // add the newly created code
  if (isCreateUserDefinedRoleSuccess(action) || isUpdateUserDefinedRoleSuccess(action)) {
    const userDefinedRole = action.payload.data;
    const code = userDefinedRole.code;
    const isAlreadyExisting = state.includes(code);
    return isAlreadyExisting ? state : [ ...state, code ];
  }

  return state;
}
function recentlyModified(
  state = INITIAL_STATE.recentlyModified,
  action: AnyAction
): UserDefinedRoles['recentlyModified'] {
  if (isClearRecentlyModified(action)) {
    return [];
  }

  // add to recently modified
  if (isCreateUserDefinedRoleSuccess(action) || isUpdateUserDefinedRoleSuccess(action)) {
    const userDefinedRole = action.payload.data;
    const code = userDefinedRole.code;
    const isAlreadyExisting = state.includes(code);
    return isAlreadyExisting ? state : [ code, ...state ];
  }

  return state;
}

export const userDefinedRoles = combineReducers({
  allCodes,
  byCode,
  recentlyModified
});
